// Die Seitenleiste — die VERWALTUNG, ausklappbar über die drei Striche.
//
// ══ Wer sieht das hier ══
// Nur Admins. Für alle anderen gibt es die Leiste nicht, und auch die drei
// Striche verschwinden — ein Knopf, der ein leeres Menü öffnet, ist schlimmer
// als kein Knopf. Die Seiten dahinter sind zusätzlich serverseitig durch RLS
// geschützt; ein verstecktes Menü ist keine Sicherheit.
//
// ══ Was hier NICHT mehr steht ══
// Die Mitarbeiter-Module (Touren, Stempeluhr, Qualität, Neuigkeiten, Kunden,
// Produkte, Profil). Die stehen auf dem Startbildschirm — für jeden sichtbar,
// ohne Umweg über ein Menü. Beides zu zeigen hieße, dieselbe Seite an zwei
// Stellen zu pflegen, und die zweite vergisst man.
//
// Das Anheften bleibt hier trotzdem: Die Verwaltungs-Seiten stehen nicht auf dem
// Kachelfeld, ließen sich also sonst gar nicht mehr in die untere Leiste legen.
//
// ══ Dauerdruck ══
// Ein langer Druck auf ein Modul öffnet ein kleines Menü: "An Leiste anheften".
// Drei Dinge müssen dabei stimmen, sonst fühlt es sich kaputt an:
//   1. Wer scrollt, hält nicht gedrückt. Bewegt sich der Finger, wird abgebrochen.
//   2. Nach dem Aufklappen darf der Finger beim Loslassen NICHT navigieren.
//   3. iOS blendet bei langem Druck sonst seine eigene Lupe/Auswahl ein.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/stores/authStore";
import { moduleFuer, MAX_PINS, type Modul } from "@/lib/module-registry";
import { usePins, useSpeicherePins } from "@/lib/pins";
import { useMeineRechte } from "@/lib/rechte";
import { useZurueckTaste } from "@/lib/zurueck-taste";
import { cn } from "@/design-system/ui";

const DRUCKDAUER = 450;

export function MenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label="Menü öffnen"
      data-testid="menu-button"
      className="flex h-11 w-11 items-center justify-center rounded-lg text-ink-300 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
        <g stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
          <path d="M4 7h16" />
          <path d="M4 12h16" />
          <path d="M4 17h16" />
        </g>
      </svg>
    </button>
  );
}

export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const rolle = useAuth((s) => s.role);
  const signOut = useAuth((s) => s.signOut);

  // Hier steht ausschließlich die Verwaltung. Alles andere lebt auf dem
  // Startbildschirm — siehe Kopfkommentar. `moduleFuer` lässt nurAdmin-Module
  // ohnehin nur für Admins durch: Selbst wenn diese Leiste jemand anders öffnen
  // könnte, bliebe sie leer.
  const { data: rechte } = useMeineRechte();
  const verwaltung = moduleFuer(rolle, rechte?.allowedModules).filter((m) => m.nurAdmin);

  const { data: pins = [] } = usePins();
  const speichern = useSpeicherePins();

  /** Welches Modul hat gerade sein Dauerdruck-Menü offen? */
  const [menuFuer, setMenuFuer] = useState<string | null>(null);

  // Zurück schließt die Leiste — wie in jeder nativen App.
  useZurueckTaste(open, onClose);
  const [hinweis, setHinweis] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Erst das kleine Menü, dann die Leiste — sonst schließt Escape beides
      // auf einmal und man verliert den Kontext.
      if (menuFuer) setMenuFuer(null);
      else onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, menuFuer]);

  useEffect(() => {
    if (!open) {
      setMenuFuer(null);
      setHinweis(null);
    }
  }, [open]);

  if (!open) return null;

  // Die neue Liste wird HIER berechnet und fertig an die Mutation gegeben —
  // nicht dort aus dem Cache abgeleitet. Sonst rechnet das optimistische Update
  // und die Mutation dasselbe Toggle zweimal, und es hebt sich auf.
  const anheften = async (m: Modul) => {
    setHinweis(null);

    const drin = pins.includes(m.id);
    const neu = drin ? pins.filter((x) => x !== m.id) : [...pins, m.id];

    if (neu.length > MAX_PINS) {
      setHinweis(`Höchstens ${MAX_PINS} Module in der Leiste — erst eins lösen.`);
      setMenuFuer(null);
      return;
    }

    setMenuFuer(null);
    try {
      await speichern.mutateAsync(neu);
    } catch (e) {
      setHinweis((e as Error).message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 animate-fade-in bg-black/60" onClick={onClose} aria-hidden />

      <nav
        role="dialog"
        aria-modal="true"
        aria-label="Verwaltung"
        className={cn(
          "relative flex h-full w-[19.5rem] max-w-[86vw] flex-col",
          "border-r border-ink-700 bg-ink-900 shadow-2xl",
          "animate-slide-in pt-[env(safe-area-inset-top)]"
        )}
      >
        <header className="flex items-center justify-between border-b border-ink-700/70 px-5 py-4">
          <div>
            <div className="text-[15px] font-semibold text-ink-50">Verwaltung</div>
            <div className="mt-0.5 text-xs text-ink-400">
              Lang drücken, um anzuheften
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Menü schließen"
            className="-mr-2 flex h-9 w-9 items-center justify-center rounded-lg text-ink-400 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-100"
          >
            ✕
          </button>
        </header>

        {hinweis && (
          <p className="mx-3 mt-3 rounded-lg border border-warn-bg/40 bg-warn-bg/10 px-3 py-2 text-sm text-warn-fg">
            {hinweis}
          </p>
        )}

        <div className="flex-1 overflow-y-auto overscroll-contain p-3">
          {verwaltung.length > 0 ? (
            <Gruppe titel="Verwaltung" hinweis="nur für Admins">
              {verwaltung.map((m) => (
                <ModulZeile
                  key={m.id}
                  modul={m}
                  angeheftet={pins.includes(m.id)}
                  menuOffen={menuFuer === m.id}
                  onMenu={() => setMenuFuer((x) => (x === m.id ? null : m.id))}
                  onMenuZu={() => setMenuFuer(null)}
                  onAnheften={() => anheften(m)}
                  onOeffnen={onClose}
                  voll={pins.length >= MAX_PINS && !pins.includes(m.id)}
                />
              ))}
            </Gruppe>
          ) : (
            <p className="px-3 py-6 text-center text-sm text-ink-500">
              Keine Verwaltungs-Seiten für dich freigeschaltet.
            </p>
          )}

        </div>

        <footer className="border-t border-ink-700/70 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button
            onClick={async () => {
              onClose();
              await signOut();
              navigate("/login", { replace: true });
            }}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors duration-fast hover:bg-ink-800"
          >
            <span className="shrink-0 text-ink-400">
              <IconAbmelden />
            </span>
            <span className="text-[15px] font-medium text-ink-200">Abmelden</span>
          </button>
        </footer>
      </nav>
    </div>
  );
}

function Gruppe({
  titel,
  hinweis,
  children,
}: {
  titel: string;
  hinweis?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-2">
      <div className="flex items-baseline gap-2 px-3 pb-1.5 pt-2">
        <span className="text-xs font-medium uppercase tracking-wider text-ink-500">{titel}</span>
        {hinweis && <span className="text-xs text-ink-600">{hinweis}</span>}
      </div>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function ModulZeile({
  modul,
  angeheftet,
  menuOffen,
  voll,
  onMenu,
  onMenuZu,
  onAnheften,
  onOeffnen,
}: {
  modul: Modul;
  angeheftet: boolean;
  menuOffen: boolean;
  voll: boolean;
  onMenu: () => void;
  onMenuZu: () => void;
  onAnheften: () => void;
  onOeffnen: () => void;
}) {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  /** Nach einem Dauerdruck darf der folgende Klick NICHT navigieren. */
  const schluckeKlick = useRef(false);
  /**
   * Chrome feuert nach einem langen Druck auf Touch ZUSÄTZLICH ein
   * contextmenu-Event. Ohne diese Sperre öffnet der Timer das Menü und das
   * contextmenu toggelt es sofort wieder zu — es blitzt kurz auf und ist weg.
   */
  const schluckeKontext = useRef(false);

  /**
   * Klick daneben schließt das Menü.
   *
   * Hier lauern gleich zwei Fallen, und die zweite hat mich erwischt:
   *
   * 1. Ein Backdrop-<div> mit onClick funktioniert NICHT. Beim Loslassen
   *    synthetisiert Chrome aus dem Touch einen click — und der landet auf dem
   *    Backdrop, der beim Öffnen gerade erst gerendert wurde. Das Menü ging auf
   *    und sofort wieder zu.
   * 2. Deshalb: auf `pointerdown` hören, nicht auf `click`. Der pointerdown des
   *    Dauerdrucks ist zu diesem Zeitpunkt längst vorbei, und der synthetische
   *    Klick danach interessiert uns nicht mehr.
   */
  useEffect(() => {
    if (!menuOffen) return;
    const daneben = (e: PointerEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      onMenuZu();
    };
    document.addEventListener("pointerdown", daneben);
    return () => document.removeEventListener("pointerdown", daneben);
  }, [menuOffen, onMenuZu]);

  const abbrechen = () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    start.current = null;
  };

  const beginnen = (x: number, y: number) => {
    start.current = { x, y };
    timer.current = window.setTimeout(() => {
      schluckeKlick.current = true;
      schluckeKontext.current = true;
      onMenu();
      // Kurz vibrieren, damit man den Druck FÜHLT und nicht rät, ob er saß.
      navigator.vibrate?.(12);

      // ══ Der Klick, der aus dem Nichts kommt ══
      //
      // Beim Loslassen baut der Browser aus dem Touch noch einen click. Der
      // landet dort, wo der Finger liegt — und dort ist inzwischen das Menü
      // aufgegangen. Ergebnis: Das Menü löst beim Loslassen SOFORT seine erste
      // Aktion aus, ohne dass man es je gesehen hat.
      //
      // Also: den nächsten Klick genau einmal abfangen, bevor er irgendwo
      // ankommt. Capture-Phase, sonst ist er schon durch.
      const schlucken = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      };
      document.addEventListener("click", schlucken, { capture: true, once: true });

      // Kommt gar kein Klick (Maus, abgebrochene Geste), darf der Fänger nicht
      // liegen bleiben — sonst frisst er irgendwann den falschen Klick.
      window.setTimeout(
        () => document.removeEventListener("click", schlucken, { capture: true }),
        800
      );
    }, DRUCKDAUER);
  };

  return (
    <li className="relative">
      <Link
        to={modul.to}
        // replace, nicht push: Die offene Leiste hat einen Zurück-Marker auf dem
        // History-Stack (useZurueckTaste). Diese Navigation ERSETZT ihn durch die
        // Zielseite, statt einen zweiten Eintrag mit derselben URL zu hinterlassen —
        // sonst bräuchte man einen zusätzlichen, wirkungslosen Zurück-Druck.
        replace
        onClick={(e) => {
          if (schluckeKlick.current) {
            // Der Druck hat das Menü geöffnet — der Finger, der losgelassen wird,
            // darf jetzt nicht auch noch navigieren.
            e.preventDefault();
            schluckeKlick.current = false;
            return;
          }
          onOeffnen();
        }}
        // Rechtsklick am Schreibtisch tut dasselbe wie der Dauerdruck am Handy.
        // Auf Touch feuert Chrome dieses Event aber AUCH nach dem langen Druck —
        // dann hat der Timer das Menü schon geöffnet, und ein zweiter Aufruf
        // würde es sofort wieder schließen. Also nur unterdrücken, nicht toggeln.
        onContextMenu={(e) => {
          e.preventDefault();
          if (schluckeKontext.current) {
            schluckeKontext.current = false;
            return;
          }
          onMenu();
        }}
        onPointerDown={(e) => {
          if (e.pointerType === "mouse") return; // Maus hat den Rechtsklick
          beginnen(e.clientX, e.clientY);
        }}
        onPointerMove={(e) => {
          // Wer scrollt, hält nicht gedrückt. 10 px Toleranz gegen Wackeln.
          if (!start.current) return;
          const d = Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y);
          if (d > 10) abbrechen();
        }}
        onPointerUp={abbrechen}
        onPointerCancel={abbrechen}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5",
          "transition-colors duration-fast hover:bg-ink-800",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
          // Ohne das zeigt iOS beim langen Druck seine eigene Lupe und den
          // Link-Callout — der Dauerdruck wäre unbenutzbar.
          "select-none [-webkit-touch-callout:none]",
          menuOffen && "bg-ink-800"
        )}
      >
        <span className="shrink-0 text-ink-400">
          <span className="block [&>svg]:h-5 [&>svg]:w-5">{modul.icon}</span>
        </span>

        {/* Nur die Überschrift. Die Erklärung darunter ("Rechnungen,
            Lieferscheine, Bargeld") stand bei jedem Eintrag und machte aus einer
            Liste von vier Punkten eine Textwand — wer das Menü öffnet, sucht
            einen Namen, nicht eine Beschreibung. */}
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="truncate text-[15px] font-medium text-ink-100">{modul.label}</span>
          {angeheftet && <IconNadel />}
        </span>
      </Link>

      {menuOffen && (
        <div
          ref={menuRef}
          role="menu"
          className={cn(
            "absolute right-2 top-1/2 z-20 min-w-[13rem] -translate-y-1/2",
            "animate-fade-in rounded-xl border border-ink-700 bg-ink-850 p-1 shadow-2xl"
          )}
        >
          {/* Das Menü legt sich über die Zeile, die man gedrückt hält — der
              Modulname wäre sonst genau von dem Ding verdeckt, das ihn betrifft.
              Also steht er hier nochmal. */}
          <div className="truncate border-b border-ink-700/70 px-3 py-2 text-xs font-medium uppercase tracking-wider text-ink-500">
            {modul.label}
          </div>

          <button
            role="menuitem"
            onClick={onAnheften}
            disabled={voll && !angeheftet}
            className={cn(
              "mt-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm",
              "transition-colors duration-fast",
              voll && !angeheftet
                ? "cursor-not-allowed text-ink-500"
                : "text-ink-100 hover:bg-ink-800"
            )}
          >
            <IconNadel />
            {angeheftet ? "Von der Leiste lösen" : "An die Leiste heften"}
          </button>

          {voll && !angeheftet && (
            <p className="px-3 pb-2 pt-1 text-xs text-ink-500">
              Die Leiste ist voll ({MAX_PINS}). Erst eins lösen.
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function IconNadel() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 text-gruen-400"
      aria-hidden
    >
      <path d="M12 17v5" />
      <path d="M9 10.5V4h6v6.5l2.5 3.5h-11L9 10.5Z" />
    </svg>
  );
}

function IconAbmelden() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 4.5H5.5a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1H9" />
      <path d="M15.5 15.5 19 12l-3.5-3.5" />
      <path d="M19 12H9" />
    </svg>
  );
}
